import { ModelIteratorBVH } from '../../../ModelIteratorBVH';
import { LoadGeometryTask } from "./LoadGeometryTask";
import { LoadRcsNodeTask } from "./LoadRcsNodeTask";
import { OutOfCoreTileManager } from '../../OutOfCoreTileManager';
import { rcsWorkerPool } from "../../../../../file-loaders/rcs/RcsWorkerPool";

/** @import {BvhNode} from "../../BvhNode" */

/**
 * Creates a task that waits until the geometry has been loaded (once the fraglist has been loaded).
 *
 * @param {BvhNode} node - The ID of the BVH node
 * @param {Stage} stage - The stage of the task
 */
export function initializeGeometryLoadedTasks(node, stage) {
  const bvhModelIterator = node.model.getIterator();

  if (!(bvhModelIterator instanceof ModelIteratorBVH)) {
    console.error("ModelIterator is not a BVH iterator");
    return;
  }

  if (node.model.loader?.supportsGeometryLoadTask) {
    node.addTask(new LoadGeometryTask(node), stage);
  }
}

/**
 * Creates a task that loads RCS octree node data on-demand
 *
 * @param {BvhNode} node - The BVH node
 * @param {Stage} stage - The stage of the task
 */
export function initializeRcsGeometryLoadedTasks(node, stage) {
  const bvhModelIterator = node.model.getIterator();

  if (!(bvhModelIterator instanceof ModelIteratorBVH)) {
    console.error("ModelIterator is not a BVH iterator");
    return;
  }

  node.addTask(new LoadRcsNodeTask(node), stage);
}

/**
 * @param {BvhNode} node - The BVH node to check prerequisites for
 * @returns {boolean} - True if all prerequisites are met, false otherwise
 */
export function geometryLoadedPrerequisites(node) {

  if (!node.model.loader?.supportsGeometryLoadTask) {
    return true;
  }

  // It can happen, that the BVH is already initialized and ready to provide batches
  // to the loader, before the request objects in the loader have been initialized.
  // Specifically, if there is a selective loading controller active, it can delay the
  // initialization of fragments and then the requests will not be created. So we have
  // to wait until the selective loading controller is ready.
  const selectiveLoadingController = node.model.loader._selectiveLoadingController;
  if (selectiveLoadingController?.isActive && !selectiveLoadingController.isReady) {
    return false;
  }

  const geomCache = node.model.loader.geomCache();
  OutOfCoreTileManager.notifyHadDownloadTasks();

  if (!geomCache.hasBandwidthBudget()) {
    return false;
  }
  return true;
}

/**
 * Prerequisites for RCS geometry loading
 * @param {BvhNode} node - The BVH node to check prerequisites for
 * @returns {boolean} - True if all prerequisites are met, false otherwise
 */
export function rcsGeometryLoadedPrerequisites(node) {
  // Cap in-flight RCS network/decoding backlog. Without this, a long queue of outstanding
  // fetches for an old view must drain before the loader can prioritize new requests for
  // the current viewpoint, so camera moves feel unresponsive.
  const MEGABYTES = 1024 * 1024;
  if (rcsWorkerPool.inFlightRequestsSize >= 20 * MEGABYTES) {
    return false;
  }

  return true;
}